import React, { useState, useCallback, useRef, useEffect} from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import './Page.css';
import { GoogleMap, useJsApiLoader, Autocomplete, Marker } from '@react-google-maps/api'
import axiosInstance from '../axiosConfig';
import {
  Typography,
  Box,
  Button,
  Paper,
  CssBaseline,
  Stack,
  Alert,
  Container,
  Fade,
  Card,
  Divider,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import ColorModeSelect from './shared-theme/ColorModeSelect';
import { useAlert } from './shared/CustomAlert';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { DateUtils } from '../utils/DateUtils';

const ConfigContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'fixed',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  minHeight: '100dvh',
  width: '100%',
  position: 'relative',
  padding: 0,
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
  [theme.breakpoints.up('sm')]: {
    padding: 0,
  },
}));

const SearchCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  background: theme.palette.background.paper,
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  '& input': {
    width: '100%',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: '1rem',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.lighter || 'rgba(33, 150, 243, 0.1)'}`,
    },
  },
}));

const MapCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  background: theme.palette.background.paper,
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
}));

const StoreDetailsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  background: theme.palette.background.paper,
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  padding: theme.spacing(3),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  boxShadow: 'none',
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #00B4D8 90%)',
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 1),
  marginBottom: theme.spacing(3),
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const containerStyle = {
    width: '100%',
    height: '300px'
}

const center = {
  lat: 47.6211,
  lng: -122.3499,
}

const libraries = ['places']; // Required for Autocomplete

function GoogleReviewQrCodeConfigPage() {
    const googleMapRef = useRef(null);
    const [qrCodeId, setQrCodeId] = useState('');
    const [isSelected, setIsSelected] = useState(false);
    const [storeName, setStoreName] = useState('');
    const [storeAddress, setStoreAddress] = useState('');
    const [storePlaceId, setStorePlaceId] = useState(null);
    const [qrCodeName, setQrCodeName] = useState('');
    const [qrCodeNameError, setQrCodeNameError] = useState('');
    const [markerPosition, setMarkerPosition] = useState(null); 
    const [qrCodeIdInvalidMessage, setQrCodeInvalidMessage] = useState('');
    const [searchParams] = useSearchParams(); // Hook to access query parameters
    const navigate = useNavigate();
    const autocompleteRef = useRef(null);
    const { showAlert } = useAlert();

    useEffect(() => {
        const qrCodeIdFromParams = searchParams.get('id');
        const qrTypeFromParams = searchParams.get('t');
        const checkQrId = async () => {
          try {
            await axiosInstance.post('/verify-qr-code', { qrCodeId: qrCodeIdFromParams, qrType: qrTypeFromParams });
          } catch (error) {
            if (error.response) {
              if (error.response.status === 404) {
                setQrCodeInvalidMessage("QR code is invalid. Please try another one.");
              } else if (error.response.status === 401) {
                setQrCodeInvalidMessage(error.response.data.error);
              } else {
                setQrCodeInvalidMessage("An error occurred while verifying the QR code. Please try again.");
              }
            } else {
              setQrCodeInvalidMessage("Network error occurred. Please check your connection and try again.");
            }
          }
        }
        if (qrCodeIdFromParams) {
            checkQrId();
            setQrCodeId(qrCodeIdFromParams);
        }
        const checkSession = async () => {
          try {
            const response = await axiosInstance.get('/auth/check-session');
            console.log('response from check-session', response)
            if (!response.data.userId) {
              // Redirect to login if no userId in session
              navigate(`/login?id=${qrCodeIdFromParams}&t=${qrTypeFromParams}`);
            }
          } catch (error) {
            console.error('Error checking session:', error);
            // Handle error (e.g., redirect to login or display an error message)
            navigate(`/login?id=${qrCodeIdFromParams}&t=${qrTypeFromParams}`);
          }
        };
        
        checkSession();
      }, [navigate, searchParams]);


    const onPlaceChanged = useCallback(() => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place && place.geometry && place.geometry.location) {
                if (!place.business_status || place.business_status !== "OPERATIONAL") {
                    showAlert("Store is not operational, please search again", 'warning');
                    return;
                }
                setIsSelected(true);
                setStoreName(place.name);
                setStoreAddress(place.formatted_address);
                setStorePlaceId(place.place_id);
                setMarkerPosition(place.geometry.location);
            } else {
                console.log('Place details not found.');
            }
        }
    }, [showAlert]);    


    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyD0NQJUlBzcYsBzbyKwximhUwB56BeS49I',
        libraries
    })
    // eslint-disable-next-line
    const [map, setMap] = React.useState(null)
    
    const onLoad = React.useCallback(function callback(map) {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude, 
                };
                map.setCenter(pos)
                },
                () => {
                    console.log("Error: The Geolocation service failed.");
                }
            );
            } else {
                console.log("Error: Your browser doesn't support geolocation.");   
            }
        map.setZoom(16)
        setMap(map)
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // Validate Stand Name
        if (!qrCodeName.trim()) {
            setQrCodeNameError('Stand Name is required');
            return;
        }
        
        try {
          const response = await axiosInstance.put('/save-qr-code', {
            qrCodeId: qrCodeId,
            qrType: "google-review",
            businessName: storeName,
            businessAddress: storeAddress,
            businessPlaceId: storePlaceId,
            qrCodeName: qrCodeName,
            formattedDate: DateUtils.getTodayFormatted()
          });
    
          if (response.status === 200) {
            showAlert("QrCode configuration saved successfully!", 'success');
            navigate('/profile');
          } 
        } catch (error) {
            if (error.response && error.response.status === 400) {
                showAlert("QR code is registered, if you want to re-register it, please delete it first in profile page", 'warning');
            }
        }
      };
    
    const onUnmount = React.useCallback(function callback(map) {
        setMap(null)
    }, [])
    
      return isLoaded ? (
        <AppTheme>
          <CssBaseline enableColorScheme />
          <ConfigContainer>
            <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1 }} />
            <StyledPaper>
              <Container maxWidth="sm" sx={{ py: 4 }}>
                <Fade in timeout={800}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <BackButton
                      component={Link}
                      to="/profile"
                      startIcon={<ArrowBackIcon />}
                    >
                      Back to Stores
                    </BackButton>

                    <Typography
                      variant="h5"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 600,
                        mb: 3,
                      }}
                    >
                      <StorefrontIcon color="primary" sx={{ fontSize: 28 }} />
                      Configure Store
                    </Typography>

                    {qrCodeIdInvalidMessage ? (
                      <Box sx={{ textAlign: 'left' }}>
                        <Alert severity="error" sx={{ mb: 3 }}>
                          {qrCodeIdInvalidMessage}
                        </Alert>
                      </Box>
                    ) : (
                      <>
                        <SearchCard elevation={0}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <SearchIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Search Your Store
                            </Typography>
                          </Box>
                          <Autocomplete
                            onLoad={(autocomplete) => {
                              autocompleteRef.current = autocomplete;
                            }}
                            onPlaceChanged={onPlaceChanged}
                          >
                            <input
                              type="text"
                              placeholder="Enter store name to search"
                              value={storeAddress}
                              onChange={(e) => setStoreAddress(e.target.value)}
                            />
                          </Autocomplete>
                        </SearchCard>

                        {isSelected && (
                          <StoreDetailsCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <LocationOnIcon color="primary" />
                              <Typography variant="subtitle1" fontWeight={600}>
                                Selected Store Details
                              </Typography>
                            </Box>
                            <Typography variant="body1" gutterBottom>
                              <strong>Business Name:</strong> {storeName}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>Business Address:</strong> {storeAddress}
                            </Typography>
                            <TextField
                              fullWidth
                              label="Stand Name"
                              value={qrCodeName}
                              onChange={(e) => {
                                setQrCodeName(e.target.value);
                                if (e.target.value.trim()) {
                                  setQrCodeNameError('');
                                }
                              }}
                              error={!!qrCodeNameError}
                              helperText={qrCodeNameError}
                              margin="normal"
                              variant="outlined"
                              required
                            />
                            <Divider sx={{ my: 2 }} />
                            <ActionButton
                              fullWidth
                              onClick={handleSubmit}
                              disabled={!storePlaceId}
                            >
                              Associate Store with QR Code
                            </ActionButton>
                          </StoreDetailsCard>
                        )}
                        <MapCard elevation={0} sx={{mt: 2 }}>
                          <GoogleMap
                            ref={googleMapRef}
                            mapContainerStyle={containerStyle}
                            center={markerPosition ? markerPosition : center}
                            onLoad={onLoad}
                            onUnmount={onUnmount}
                            options={{
                              streetViewControl: false,
                              scaleControl: false,
                              mapTypeControl: false,
                              zoomControl: false,
                              panControl: false,
                              rotateControl: false,
                              fullscreenControl: false
                            }}
                          >
                            {markerPosition && <Marker position={markerPosition} />}
                          </GoogleMap>
                        </MapCard>                        
                      </>
                    )}
                  </Box>
                </Fade>
              </Container>
            </StyledPaper>
          </ConfigContainer>
        </AppTheme>
      ) : <div>Loading...</div>;
}

export default GoogleReviewQrCodeConfigPage;
